import { useCallback, useEffect, useRef } from 'react';
import LocalStorage from '../../common/LocalStorage';
import useProject from '../useProject';
import useUrlState from '../../common/useUrlState';

/**
 * Stores the 'event' URL parameter in local storage. If the URL does
 * not have an 'event' parameter, it is retrieved from local storage.
 */
const useRecentEventId = () => {
  const project = useProject();
  const storageKey = `atleta:${project.id}:recent-event`;

  /**
   * Retrieves the last event ID that was stored in
   * the local storage for the current project.
   */
  const getRecentEventIdFromLocalStorage = useCallback(
    () => LocalStorage.getItem(storageKey),
    [storageKey],
  );

  const [urlState, setUrlState] = useUrlState({
    parse: (params, { string }) => ({
      ...params,
      event: string(params.event),
    }),
  });

  const previousEventIdRef = useRef(urlState.event);

  if (urlState.event) {
    // If an 'event' ID is present in the URL, remember it.
    LocalStorage.setItem(storageKey, urlState.event);
  } else if (previousEventIdRef.current) {
    // If an event ID was present in the URL during a previous render,
    // forget the stored event ID.
    LocalStorage.removeItem(storageKey);
  }

  const storedEventId = getRecentEventIdFromLocalStorage();

  useEffect(() => {
    if (!urlState.event && storedEventId) {
      setUrlState((params) => ({ ...params, event: storedEventId }), true);
    }

    previousEventIdRef.current = urlState.event;
  }, [urlState.event, setUrlState, storedEventId]);

  /**
   * Removes the recent event ID from the URL and local storage.
   */
  const reset = () => {
    setUrlState((params) => ({ ...params, event: undefined }), true);
    LocalStorage.removeItem(storageKey);
  };

  return {
    id: urlState.event || storedEventId,
    getRecentEventIdFromLocalStorage,
    reset,
  };
};

export default useRecentEventId;
